import { Iconify } from '@sohanemon/utils/components';
import { Chip } from '@sohanscript/ui/chip';
import { ImageInspector } from '@sohanscript/ui/image-inspector';
import { List, ListItem } from '@sohanscript/ui/list';
import { PageHero } from '@sohanscript/ui/page-hero';
import { createFileRoute } from '@tanstack/react-router';
import { Link, type LinkTo } from '@/components/link';

export const Route = createFileRoute('/admin/')({
	component: AdminIndexPage,
});

type AdminLink = {
	title: string;
	description: string;
	href: LinkTo;
	icon: string;
	external?: boolean;
};

function AdminLinkRow({ title, description, href, icon, external }: AdminLink) {
	return (
		<ListItem as="li">
			<Link
				to={href}
				target={external ? '_blank' : undefined}
				rel={external ? 'noreferrer' : undefined}
				className="flex items-center gap-4 px-1 py-4"
			>
				<span className="grid size-10 shrink-0 place-items-center rounded-lg border border-primary/20 bg-primary/10 text-primary transition-colors duration-300 group-hover:border-primary/50 group-hover:bg-primary/15">
					<Iconify icon={icon} className="size-5" aria-hidden="true" />
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<span className="font-medium text-base text-foreground transition-colors duration-300 group-focus-within:text-primary group-hover:text-primary">
							{title}
						</span>
						{external && <Chip className="shrink-0">external</Chip>}
					</div>
					<p className="mt-1 text-muted-foreground text-sm leading-relaxed">
						{description}
					</p>
				</div>
				<Iconify
					icon={external ? 'lucide:arrow-up-right' : 'lucide:arrow-right'}
					className="size-4 shrink-0 text-muted-foreground transition-transform transition-colors duration-300 group-hover:translate-x-0.5 group-hover:text-primary rtl:-scale-x-100"
					aria-hidden="true"
				/>
			</Link>
		</ListItem>
	);
}

function AdminIndexPage() {
	const links: AdminLink[] = [
		{
			title: 'Markdown playground',
			description:
				'Preview directives, code blocks, and syntax before publishing.',
			href: '/admin/play-md',
			icon: 'lucide:terminal',
		},
		{
			title: 'Markdown gallery',
			description: 'Every supported markdown feature, rendered live.',
			href: '/admin/example-md',
			icon: 'lucide:book-open',
		},
		{
			title: 'Backlog',
			description: 'Ranked technical topics waiting to become notes.',
			href: '/admin/backlog',
			icon: 'lucide:notebook',
		},
		{
			title: 'Assets',
			description: 'Browse Sanity CDN files and download brand assets.',
			href: '/admin/assets',
			icon: 'lucide:folder',
		},
		{
			title: 'Sanity Studio',
			description: 'Edit notes, projects, and site content.',
			href: '/db',
			icon: 'simple-icons:sanity',
			external: true,
		},
		{
			title: 'Vercel',
			description: 'Deployments, logs, and environment variables.',
			href: '/vercel',
			icon: 'akar-icons:vercel-fill',
			external: true,
		},
	];

	return (
		<div className="pb-10">
			<section className="container py-10 lg:py-20">
				<ImageInspector
					src={
						'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTDYj84nr5z5qKRE82g8hhMMj33wHl_KJcwRHLvwP2VhLXcIncWC5VB1yc&s=10'
					}
				>
					hello
				</ImageInspector>
				<PageHero
					path="admin"
					status="[STATUS: PRIVATE]"
					sub="Everything that runs this site: content, previews, and deployments."
					meta="Workspace"
					metaRight="NODE: _ADMIN // 0x00"
				>
					Admin
				</PageHero>
				<List as="ul">
					{links.map((link) => (
						<AdminLinkRow key={link.href} {...link} />
					))}
				</List>
			</section>
		</div>
	);
}
